/**
 * Landing Circuito Lunins — lunins.org/circuito
 * Apresentação B2G: Circuito Lunins de Cuidado com o Servidor.
 */

export const CIRCUITO_WA_E164 =
  import.meta.env.VITE_CIRCUITO_WA_E164?.trim() || '5567992089769';

export type CircuitoMidiaItem = {
  src: string;
  alt: string;
  caption?: string;
  type: 'image' | 'video';
};

export type CircuitoDepoimento = {
  quote: string;
  nome: string;
  org: string;
  cargo?: string;
};

export type CircuitoCidade = {
  cidade: string;
  uf: string;
  descricao: string;
  autorizado: boolean;
};

export const CIRCUITO_LANDING = {
  slug: 'circuito',
  pageTitle: 'Circuito Lunins de Cuidado com o Servidor | Órgãos públicos',
  metaDescription:
    'A Lunins planeja e executa circuitos de salas de descompressão que percorrem as unidades da sua rede — aproximando autocuidado da rotina do servidor público.',

  hero: {
    eyebrow: 'Lunins · Cuidado mais perto de quem cuida',
    headline: 'A Lunins leva o cuidado até onde o servidor está.',
    subheadline:
      'Criamos circuitos de salas de descompressão que percorrem as unidades e aproximam experiências de autocuidado da realidade do dia a dia do servidor.',
    taglines: [
      'Uma pausa no lugar onde a rotina acontece.',
      'O autocuidado não precisa estar longe da rotina.',
      'Levamos a experiência até onde as pessoas estão.',
    ],
    cta: 'Montar o circuito da minha rede',
    ctaHash: '#formulario',
  },

  contexto: {
    id: 'contexto',
    title: 'Cuidar de quem cuida',
    lead:
      'A instituição cria condições para que pequenos momentos de cuidado também encontrem espaço dentro da rotina — combinando cultura institucional de cuidado com oportunidade individual de autocuidado.',
    manifesto: [
      'O autocuidado precisa caber na vida real.',
      'Nem sempre o servidor consegue sair da unidade.',
      'Nem sempre consegue reservar uma tarde inteira.',
      'Nem sempre todos os turnos alcançam uma ação centralizada.',
      'Por isso, nossa proposta segue outro caminho: aproximamos a experiência da rotina.',
      'Chegamos à unidade. Preparamos o ambiente. Criamos um espaço para pausa.',
      'E deixamos que o cuidado encontre as pessoas no lugar onde o dia realmente acontece.',
      'Essa é a lógica do Circuito Lunins: menos distância entre o servidor e a oportunidade de cuidar de si.',
    ],
  },

  circuito: {
    id: 'circuito',
    title: 'O Circuito Lunins',
    lead:
      'A Lunins estrutura, planeja e executa um cronograma de ações percorrendo diferentes unidades vinculadas ao órgão público. Em cada data programada, a equipe chega à unidade, monta temporariamente a experiência e mantém o ambiente disponível durante o período definido.',
    fluxo: [
      { label: 'Secretaria', desc: 'Define unidades e objetivo institucional.' },
      { label: 'Mapeamento', desc: 'Identificamos a rede a ser alcançada.' },
      { label: 'Cronograma', desc: 'Programação personalizada por unidade.' },
      { label: 'Unidades', desc: 'Cada local recebe sua data ou período.' },
      { label: 'Relatório', desc: 'Registro dos indicadores acordados.' },
    ],
    etapas: [
      {
        num: '01',
        titulo: 'Mapeamos a rede',
        texto:
          'A Secretaria informa as unidades que deseja alcançar — hospitais, escolas, CRAS, centros administrativos e demais equipamentos da rede.',
        exemplos: {
          saude: ['Hospitais', 'Unidades de saúde', 'Centros especializados', 'Unidades administrativas', 'Serviços vinculados'],
          educacao: ['Escolas', 'Centros de educação', 'Unidades administrativas', 'Demais equipamentos da Secretaria'],
          assistencia: ['CRAS', 'CREAS', 'Unidades de acolhimento', 'Centros de atendimento', 'Unidades administrativas'],
        },
      },
      {
        num: '02',
        titulo: 'Construímos o cronograma',
        texto:
          'Lunins e órgão definem a programação considerando quantidade de unidades, distância, servidores, turnos, horários, capacidade da equipe e tempo de montagem/desmontagem.',
      },
      {
        num: '03',
        titulo: 'A Lunins chega à unidade',
        texto:
          'A equipe realiza a implantação temporária: chegada, montagem, ambientação e experiência pronta para uso.',
      },
      {
        num: '04',
        titulo: 'A sala fica disponível',
        texto:
          'Durante o período programado, os servidores acessam o espaço conforme dinâmica definida com a instituição — respeitando sempre o funcionamento da unidade.',
      },
      {
        num: '05',
        titulo: 'Experiências',
        texto:
          'O ambiente pode permanecer disponível durante toda a programação; atendimentos com capacidade limitada (como massagens) funcionam por fluxo, horário ou agenda.',
      },
      {
        num: '06',
        titulo: 'Finalização',
        texto:
          'Experiência encerrada, estrutura desmontada, espaço devolvido, indicadores registrados e equipe segue para a próxima unidade do circuito.',
      },
    ],
  },

  cronograma: {
    id: 'cronograma',
    title: 'Um circuito que percorre a rede',
    lead: 'Exemplo ilustrativo de como uma Secretaria pode distribuir a ação ao longo da semana.',
    disclaimer:
      'Cronograma ilustrativo. A programação é personalizada de acordo com a rede e a operação de cada órgão.',
    semanas: [
      {
        titulo: 'Semana 01',
        dias: [
          { dia: 'SEG', unidade: 'Hospital / Unidade 01', horario: '08h–17h' },
          { dia: 'TER', unidade: 'Unidade 02', horario: '08h–17h' },
          { dia: 'QUA', unidade: 'Unidade 03', horario: '08h–17h' },
          { dia: 'QUI', unidade: 'Unidade 04', horario: '08h–17h' },
          { dia: 'SEX', unidade: 'Unidade 05', horario: '08h–17h' },
        ],
      },
      {
        titulo: 'Semana 02',
        dias: [{ dia: '—', unidade: 'Continuidade do circuito nas demais unidades da rede', horario: 'Conforme planejamento' }],
      },
    ],
  },

  diaExperiencia: {
    id: 'dia',
    title: 'O dia da experiência',
    lead: 'Valorizamos a capacidade operacional da Lunins em cada unidade visitada.',
    passos: [
      { titulo: 'Chegada', desc: 'Equipe e materiais no local acordado.' },
      { titulo: 'Montagem', desc: 'Estrutura temporária instalada no espaço disponível.' },
      { titulo: 'Ambientação', desc: 'Ambiente preparado para pausa e autocuidado.' },
      { titulo: 'Experiência pronta', desc: 'Espaço aberto conforme horários definidos.' },
      { titulo: 'Funcionamento', desc: 'Servidores utilizam nas pausas e intervalos possíveis.' },
      { titulo: 'Desmontagem', desc: 'Espaço devolvido ao final da programação.' },
    ],
  },

  sala: {
    id: 'sala',
    title: 'Sala de Descompressão',
    lead:
      'Em cada unidade, a Lunins transforma temporariamente um espaço disponível em um ambiente pensado para pausa e autocuidado. A sala é uma experiência temporária e itinerante — o ambiente do circuito.',
    nota: 'Elementos e ativações dependem da configuração contratada para cada projeto.',
    zonas: [
      { titulo: 'Chegada e transição', desc: 'Ritual de entrada que sinaliza afastamento da rotina operacional.' },
      { titulo: 'Convivência leve', desc: 'Espaço de pausa qualificada com conforto e acolhimento.' },
      { titulo: 'Relaxamento', desc: 'Equipamentos Lunins de massagem e vibroterapia, conforme projeto.' },
      { titulo: 'Estação sensorial', desc: 'Iluminação, aromaterapia leve e estímulos de pausa, quando previstos.' },
    ],
    elementos: [
      'Ambientação e conforto',
      'Relaxamento e massagem',
      'Estímulos sensoriais',
      'Experiências de pausa',
      'Conteúdos de autocuidado',
      'Outras ativações Lunins',
    ],
  },

  experiencias: {
    id: 'experiencias',
    title: 'O que o servidor encontra',
    lead: 'Diferenciamos o que fica disponível de forma contínua do que exige organização de fluxo.',
    tipos: [
      {
        titulo: 'Experiências de acesso ao ambiente',
        desc: 'Sala e ambientação podem permanecer disponíveis durante toda a programação — convite à pausa no ritmo de cada equipe.',
        icon: 'door' as const,
      },
      {
        titulo: 'Atendimentos com capacidade limitada',
        desc: 'Massagens e outras ativações funcionam por organização de fluxo, horário ou agenda — conforme capacidade e natureza da experiência.',
        icon: 'calendar' as const,
      },
    ],
  },

  comoParticipa: {
    id: 'participa',
    title: 'Como participa',
    lead:
      'A experiência se adapta ao cotidiano do servidor — e não exige que o cotidiano se adapte à experiência.',
    momentos: [
      'Durante pausas',
      'Entre atendimentos, quando operacionalmente possível',
      'Em intervalos',
      'Nas transições de turno',
      'Antes ou depois da jornada, conforme programação',
      'Em horários organizados pela própria instituição',
    ],
    nota: 'Não pressupomos que todos possam abandonar simultaneamente seus postos. A proposta respeita o funcionamento da unidade.',
  },

  formatos: {
    id: 'formatos',
    title: 'Formatos do circuito',
    lead:
      'Dependendo do dimensionamento do projeto, equipe disponível e contratação aprovada, a Lunins pode estruturar até 2 ou 3 salas funcionando simultaneamente.',
    nota: 'A operação simultânea depende de dimensionamento, equipe, estrutura, logística, distância entre unidades, formato das experiências e público estimado.',
    cards: [
      {
        id: 'essencial',
        titulo: 'Circuito Essencial',
        salas: 1,
        desc: 'Uma sala em operação por vez.',
        exemplo: 'Seg → Unidade A · Ter → Unidade B · Qua → Unidade C · Qui → Unidade D · Sex → Unidade E',
      },
      {
        id: 'ampliado',
        titulo: 'Circuito Ampliado',
        salas: 2,
        desc: 'Duas salas simultâneas em unidades diferentes.',
        exemplo: 'Seg: Sala 01 → Unidade A · Sala 02 → Unidade B',
        featured: true,
      },
      {
        id: 'expandido',
        titulo: 'Circuito Expandido',
        salas: 3,
        desc: 'Até três frentes simultâneas, conforme capacidade operacional contratada.',
        exemplo: 'Seg: Sala 01 → Hospital A · Sala 02 → Unidade B · Sala 03 → Unidade C',
      },
    ],
    dependencias: [
      'Dimensionamento do projeto',
      'Equipe disponível',
      'Estrutura e logística',
      'Distância entre unidades',
      'Formato das experiências',
      'Público estimado',
      'Contratação aprovada',
    ],
  },

  beneficios: {
    id: 'beneficios',
    title: 'Proposta de valor institucional',
    lead: 'Em vez de concentrar centenas de servidores em um único endereço, a experiência pode percorrer a própria rede.',
    items: [
      { titulo: 'Capilaridade', desc: 'Possibilidade de alcançar diferentes unidades da rede.' },
      { titulo: 'Velocidade de cobertura', desc: 'Projetos maiores podem avançar em menos tempo com múltiplas frentes simultâneas.' },
      { titulo: 'Proximidade', desc: 'A experiência chega mais perto de quem trabalha em cada unidade.' },
      { titulo: 'Flexibilidade', desc: 'Cronograma considera hospitais, UBS, escolas, CRAS e estruturas administrativas.' },
      { titulo: 'Continuidade', desc: 'A campanha ganha presença ao longo de diferentes dias e locais — não apenas um evento isolado.' },
      { titulo: 'Acessibilidade operacional', desc: 'A participação pode ser integrada às pausas e turnos.' },
      { titulo: 'Organização', desc: 'Calendário, implantação e operação com equipe Lunins dedicada.' },
      { titulo: 'Visibilidade institucional', desc: 'A política de valorização se materializa em experiência perceptível.' },
      { titulo: 'Mensuração', desc: 'A execução pode gerar dados operacionais consolidados.' },
    ],
  },

  midiaAcao: {
    id: 'midia',
    title: 'Lunins em ação',
    lead: 'Registros reais de circuitos e salas em operação.',
    items: [] as CircuitoMidiaItem[],
  },

  experienciaInstitucional: {
    id: 'institucional',
    title: 'Experiência institucional',
    lead: 'Atuação documentada conforme autorização de cada órgão.',
    cidades: [] as CircuitoCidade[],
  },

  depoimentos: {
    id: 'depoimentos',
    title: 'Depoimentos',
    lead: 'Relatos reais de gestores e servidores — publicados somente com autorização.',
    items: [] as CircuitoDepoimento[],
  },

  areas: {
    id: 'areas',
    title: 'Aplicação por secretaria',
    blocos: [
      {
        id: 'saude',
        titulo: 'Para Saúde',
        headline: 'Cuidado para quem faz o cuidado acontecer todos os dias.',
        texto:
          'Hospitais, unidades, plantões, equipes assistenciais, serviços administrativos e diferentes turnos. A Lunins percorre as unidades — o servidor não precisa procurar a ação; a ação chega até sua unidade.',
        equipamentos: ['Hospitais', 'Unidades de saúde', 'Centros especializados', 'Serviços administrativos'],
      },
      {
        id: 'educacao',
        titulo: 'Para Educação',
        headline: 'Cuidar de quem faz a educação acontecer.',
        texto:
          'Professores, coordenadores, diretores, administrativos, equipes de apoio e demais servidores. O Circuito Lunins percorre unidades escolares e administrativas conforme planejamento.',
        equipamentos: ['Escolas', 'Centros de educação', 'Unidades administrativas', 'Equipamentos da Secretaria'],
      },
      {
        id: 'assistencia',
        titulo: 'Para Assistência Social',
        headline: 'Cuidar de quem acolhe e cuida de pessoas todos os dias.',
        texto:
          'CRAS, CREAS, centros, unidades administrativas e demais serviços da rede. Valorizamos a proximidade da experiência e sua adaptação à rotina das equipes.',
        equipamentos: ['CRAS', 'CREAS', 'Centros de acolhimento', 'Unidades administrativas'],
      },
    ],
  },

  setembroAmarelo: {
    id: 'setembro',
    title: 'Setembro Amarelo que percorre a rede',
    lead:
      'Em vez de realizar apenas uma ação central, o órgão pode estabelecer um cronograma para levar a experiência a diferentes unidades ao longo do mês.',
    mensagens: [
      'Durante o Setembro Amarelo, o cuidado pode sair da comunicação e percorrer a rede.',
      'Uma campanha que não acontece apenas em um dia ou em um lugar.',
    ],
    formatos: [
      '1 sala por dia',
      '2 salas em unidades diferentes simultaneamente',
      'Até 3 frentes simultâneas, conforme dimensionamento',
    ],
    exemploSemanas: [
      { semana: 'Semana 1', regiao: 'Unidades da Região A' },
      { semana: 'Semana 2', regiao: 'Unidades da Região B' },
      { semana: 'Semana 3', regiao: 'Unidades da Região C' },
      { semana: 'Semana 4', regiao: 'Unidades da Região D' },
    ],
    disclaimer: 'Exemplo hipotético. Cada cronograma é dimensionado individualmente.',
    escopo: [
      'Conscientização',
      'Autocuidado',
      'Valorização',
      'Cultura de cuidado',
      'Informação responsável',
      'Busca apropriada de apoio quando necessária',
    ],
    nota:
      'Não associamos sala de descompressão ou massagem à prevenção isolada de situações graves de saúde mental.',
  },

  mensuracao: {
    id: 'mensuracao',
    title: 'Mensuração',
    lead: 'Indicadores operacionais acordados com a gestão — linguagem de registro, não promessa clínica.',
    indicadores: [
      'Unidades visitadas no período',
      'Dias de operação por unidade',
      'Público estimado alcançado',
      'Registros de acesso ao ambiente',
      'Atendimentos com capacidade limitada (quando aplicável)',
      'Satisfação percebida (quando acordado)',
      'Consolidação em relatório da ação',
    ],
    nota: 'Indicadores definidos em conjunto com o órgão contratante. Não prometemos benefícios clínicos ou de produtividade.',
  },

  form: {
    id: 'formulario',
    title: 'Vamos montar o circuito da sua rede?',
    lead: 'Com estas informações, a Lunins pode estruturar sugestão de cronograma, dimensionamento, logística, equipe, experiências e proposta comercial.',
    fields: {
      orgao: 'Órgão / Secretaria',
      orgaoOptions: [
        'Secretaria de Saúde',
        'Secretaria de Educação',
        'Secretaria de Assistência Social',
        'Outro',
      ],
      municipio: 'Município / Estado',
      unidades: 'Quantidade aproximada de unidades',
      servidores: 'Quantidade aproximada de servidores',
      turnos: 'Principais turnos',
      periodo: 'Período desejado',
      objetivo: 'Objetivo do projeto',
      objetivoOptions: [
        'Ação pontual',
        'Campanha',
        'Setembro Amarelo',
        'Circuito',
        'Programa recorrente',
      ],
    },
    submit: 'Enviar pelo WhatsApp',
    waTemplate: `Olá! Vim pelo site Lunins (/circuito) e gostaria de estruturar um Circuito Lunins.

*Órgão:* {{orgao}}
*Município/UF:* {{municipio}}
*Unidades (aprox.):* {{unidades}}
*Servidores (aprox.):* {{servidores}}
*Turnos:* {{turnos}}
*Período:* {{periodo}}
*Objetivo:* {{objetivo}}

Aguardo retorno para montarmos o cronograma.`,
  },

  faq: {
    id: 'faq',
    title: 'Perguntas frequentes',
    items: [
      {
        pergunta: 'O que é o Circuito Lunins?',
        resposta:
          'É um cronograma de ações em que a Lunins percorre unidades da rede do órgão público, montando temporariamente salas de descompressão e experiências de autocuidado em cada local programado.',
      },
      {
        pergunta: 'Preciso de um espaço específico em cada unidade?',
        resposta:
          'Sim, um ambiente disponível acordado com a gestão local — sala, auditório ou área equivalente. Dimensionamos conforme características de cada espaço.',
      },
      {
        pergunta: 'Quanto tempo dura a ação em cada unidade?',
        resposta:
          'Depende do projeto: pode ser um dia inteiro, meio período ou sequência de dias. O cronograma é personalizado.',
      },
      {
        pergunta: 'É possível operar em mais de uma unidade no mesmo dia?',
        resposta:
          'Sim, conforme dimensionamento e contratação. Projetos ampliados podem prever até 2 ou 3 salas simultâneas em unidades diferentes.',
      },
      {
        pergunta: 'Todos os servidores podem participar ao mesmo tempo?',
        resposta:
          'Não necessariamente. A dinâmica respeita pausas, intervalos e turnos — definidos com a instituição para não interferir na operação.',
      },
      {
        pergunta: 'O que fica disponível o tempo todo e o que exige agenda?',
        resposta:
          'O ambiente e a ambientação podem ficar abertos durante a programação. Massagens e ativações com capacidade limitada funcionam por fluxo ou horário.',
      },
      {
        pergunta: 'Quais indicadores são registrados?',
        resposta:
          'Indicadores operacionais acordados previamente — unidades visitadas, registros de acesso, relatório consolidado. Sem promessa de resultado clínico.',
      },
    ],
  },

  ctaFinal: {
    title: 'Vamos desenhar o circuito da sua Secretaria?',
    lead: 'Conte-nos sobre sua rede. A Lunins planeja e executa o circuito com você.',
    cta: 'Falar com a equipe',
    ctaHash: '#formulario',
  },
} as const;
